import React, { useEffect, useRef, useState } from "react";
import BarChart from "./BarChart";
import { fetchOutletBehaviorBarChartData } from "./barChartUtils";
import useApi from "../../../../Hooks/useApi";
import LoadingSpinner from "../../../Shared/LoadingSpinner";
import ChartExportAsPNG from "../../../../Utils/ChartExportAsPng";
import { NoDataBox } from "../NoDataFoundMessage";


const BarContainer = ({ width, height, surveyId, locationId, cityData = null }) => {
  const [barData, setBarData] = useState([]); 
  const [totalValue, setTotalValue] = useState(400);
  const [error, setError] = useState(null); 

  const { request, loading } = useApi({ isPublic: true });
  
  const downloadPngRef = useRef(null);

  useEffect(() => {

    setBarData([]);
    setError(null);
    setTotalValue(400); 

    // 2. Input Validation
    if (!surveyId || !locationId) {
      return;
    }

    const fetchData = async () => {
      try {
        const res = await fetchOutletBehaviorBarChartData(
          { surveyId, locationId },
          request
        );

       
        if (!res) {
          throw new Error("Received empty response from server.");
        }

        if (res.data && Array.isArray(res.data)) {
           setBarData(res.data);
        } else {
           // If data is missing/invalid structure, treat as empty
           setBarData([]); 
        }

        // Safety: Ensure totalValue is a number
        setTotalValue(Number(res?.totalData) || 0);

      } catch (err) {
        console.error("BarChart Container Error:", err);
        setError("Unable to load chart data.");
        setBarData([]);
      }
    };

    fetchData();
  }, [surveyId, locationId, request]);

  // --- Safe String Generation for Export ---
  const getSafeExportProps = () => {
    const cityName = cityData?.city?.name || 'City';
    const year = cityData?.year || 'Year';
    
    // Safely handle capitalization of Type
    let typeFormatted = "";
    if (cityData?.city?.type) {
      const typeStr = String(cityData.city.type);
      typeFormatted = typeStr.charAt(0).toUpperCase() + typeStr.slice(1).toLowerCase();
    }

    return {
      fileName: `Containment Outlet Behaviour - ${cityName} - ${year}`,
      imageSource: `${cityName} ${typeFormatted} - ${year}`.trim(),
    };
  };

  const exportProps = getSafeExportProps();


  if (loading) {
    return <LoadingSpinner message="Loading Bar Chart..." />;
  }

  if (error) {
    return (
      <div className="w-full p-4 border border-red-200 bg-red-50 rounded text-center">
        <p className="text-red-500 font-semibold">{error}</p>
      </div>
    );
  }

  if (!barData || barData.length === 0) {
    return (
    
    <NoDataBox/>
    );
  }


  return (
    <>
      <button
        className="w-[100px] bg-[#2f4b7c] text-white font-bold text-xs py-2 rounded hover:bg-[#1d3557] flex items-center justify-center transition-colors"
        onClick={() => {
          // Safety: Check if ref is ready
          if (downloadPngRef.current?.download) {
            downloadPngRef.current.download();
          } else {
            console.warn("Export module not ready");
          }
        }}
        title="Download as PNG"
      >
        Export as PNG
      </button>

      <div className="max-w-[1000px] mx-auto">
        <div className="containment_outlet" id="containment_outlet">
          <BarChart
            width={width}
            height={height}
            barData={barData}
            totalValue={totalValue}
            blackKey="backLabel"
            grayKey="grayLabel"
          />
        </div>

        <ChartExportAsPNG
          ref={downloadPngRef}
          targetId="containment_outlet"
          targetWidth={600}
          fileNamePrefix={exportProps.fileName}
          imageTitle="Containment Outlet Behaviour - Bar Chart"
          imageSource={exportProps.imageSource}
          titleFont="bold 20px Arial"
          sourceFont="italic 18px Arial"
          table={false}
        />
      </div>
    </>
  );
};

export default BarContainer;